import type { Event, StackFrame } from '@sentry/react';

const PRODUCT_FRUITS_VENDOR_SCRIPT_PATTERN = /^https:\/\/app\.productfruits\.com\/static\/chunks\//;
const CURIUM_TRPC_URL_PATTERN = /^https:\/\/(?:app|staging)\.curium\.app\/trpc(?:\/|$)/;
const TRANSIENT_EDGE_HTTP_STATUS_CODES = [502, 503, 504, 520, 521, 522, 523, 524] as const;
const CURIUM_SOURCE_FRAME_PATTERNS = [
    /(?:^|\/)client\/src\//,
    /^src\//,
    /^app:\/\/\/(?:\.\/)?src\//,
    /^webpack:\/\/\/?(?:[^/]+\/)?(?:\.\/)?src\//,
    /^https:\/\/(?:app|staging)\.curium\.app\/src\//,
] satisfies RegExp[];

type ExceptionValue = NonNullable<NonNullable<Event['exception']>['values']>[number];

function getExceptionValues(event: Event): ExceptionValue[] {
    return event.exception?.values ?? [];
}

function getExceptionFrames(exception: ExceptionValue): StackFrame[] {
    return exception.stacktrace?.frames ?? [];
}

function getFrameLocations(frame: StackFrame): string[] {
    return [frame.abs_path, frame.filename].filter((location): location is string => Boolean(location));
}

function hasProductFruitsVendorFrame(frames: StackFrame[]): boolean {
    return frames.some((frame) =>
        getFrameLocations(frame).some((location) => PRODUCT_FRUITS_VENDOR_SCRIPT_PATTERN.test(location)),
    );
}

function hasCuriumSourceFrame(frames: StackFrame[]): boolean {
    return frames.some((frame) =>
        getFrameLocations(frame).some((location) =>
            CURIUM_SOURCE_FRAME_PATTERNS.some((pattern) => pattern.test(location)),
        ),
    );
}

function isMaximumCallStackRangeError(exception: ExceptionValue): boolean {
    return exception.type === 'RangeError' && exception.value?.includes('Maximum call stack size exceeded') === true;
}

function isCloudflareBeaconScriptLoadError(event: Event): boolean {
    return (
        event.exception?.values?.[0]?.type === 'ScriptLoadError' &&
        event.tags?.script?.toString().includes('cloudflareinsights.com') === true
    );
}

function getExceptionMechanismType(event: Event): string | undefined {
    return event.exception?.values?.[0]?.mechanism?.type;
}

function getResponseStatusCode(event: Event): number | undefined {
    const response = event.contexts?.response;
    if (!response || typeof response !== 'object') return undefined;

    const statusCode = (response as { status_code?: unknown }).status_code;
    if (typeof statusCode === 'number') return statusCode;
    if (typeof statusCode !== 'string') return undefined;

    const parsed = Number(statusCode);
    return Number.isInteger(parsed) ? parsed : undefined;
}

function isTransientEdgeStatusCode(statusCode: number | undefined): boolean {
    return TRANSIENT_EDGE_HTTP_STATUS_CODES.includes(statusCode as (typeof TRANSIENT_EDGE_HTTP_STATUS_CODES)[number]);
}

function isCuriumTrpcUrl(url: string | undefined): boolean {
    return url !== undefined && CURIUM_TRPC_URL_PATTERN.test(url);
}

function isHttpClientMechanism(mechanismType: string | undefined): boolean {
    // httpClientIntegration tags the mechanism as `auto.http.client.${transport}`
    // (e.g. `auto.http.client.fetch`, `auto.http.client.xhr`).
    return mechanismType?.startsWith('auto.http.client.') === true;
}

function isTransientCuriumTrpcHttpClientError(event: Event): boolean {
    return (
        isHttpClientMechanism(getExceptionMechanismType(event)) &&
        isCuriumTrpcUrl(event.request?.url) &&
        isTransientEdgeStatusCode(getResponseStatusCode(event))
    );
}

function isProductFruitsVendorStackOverflow(event: Event): boolean {
    return getExceptionValues(event).some((exception) => {
        const frames = getExceptionFrames(exception);

        return (
            isMaximumCallStackRangeError(exception) &&
            hasProductFruitsVendorFrame(frames) &&
            !hasCuriumSourceFrame(frames)
        );
    });
}

export function filterSentryEvent<TEvent extends Event>(event: TEvent): TEvent | null {
    if (isCloudflareBeaconScriptLoadError(event)) {
        return null;
    }

    if (isProductFruitsVendorStackOverflow(event)) {
        return null;
    }

    if (isTransientCuriumTrpcHttpClientError(event)) {
        return null;
    }

    return event;
}
